import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Quote, Loader2, Flame, FileText, ArrowUpRight } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { api } from "@/lib/api";

const EASE = [0.22, 1, 0.36, 1];
const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InspirationsPage() {
  const [stories, setStories] = useState(null);

  useEffect(() => {
    api.get("/stories").then((r) => setStories(r.data)).catch(() => setStories([]));
  }, []);

  return (
    <div data-testid="inspirations-page" className="min-h-screen bg-slate-50">
      <Navbar />
      <main className="mx-auto max-w-4xl px-6 pb-28 pt-32 sm:px-10">
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE }}
          className="flex items-center gap-2 font-mono text-[11px] font-semibold uppercase tracking-[0.3em] text-amber-700"
        >
          <Flame size={13} /> Inspirations
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.8, ease: EASE }}
          className="mt-4 font-odia text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl"
        >
          ତମସୋ ମା ଜ୍ୟୋର୍ତିଗମୟ ।
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.8, ease: EASE }}
          className="mt-3 font-display text-xl italic text-sky-700 sm:text-2xl"
        >
          From darkness, lead me to light.
        </motion.p>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.28, duration: 0.8, ease: EASE }}
          className="mt-5 max-w-xl text-base leading-relaxed text-slate-600"
        >
          Stories from our classrooms and beyond — small lamps that kept burning.
        </motion.p>

        {stories === null ? (
          <div className="mt-16 flex items-center gap-3 text-slate-500" data-testid="stories-loading">
            <Loader2 size={18} className="animate-spin" /> Loading…
          </div>
        ) : stories.length === 0 ? (
          <p className="mt-16 rounded-3xl border border-dashed border-slate-300 p-10 text-center text-slate-500" data-testid="stories-empty">
            Inspirational stories will appear here once shared by the admin.
          </p>
        ) : (
          <div className="mt-14 space-y-8">
            {stories.map((s, i) => (
              <motion.article
                key={s.id}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ delay: i * 0.06, duration: 0.7, ease: EASE }}
                data-testid={`story-card-${i}`}
                className="relative overflow-hidden rounded-3xl border border-slate-200 bg-white p-8 shadow-sm sm:p-10"
              >
                <span aria-hidden className="absolute -right-6 -top-6 h-24 w-24 rounded-full bg-amber-100/70 blur-2xl" />
                {s.kind === "pdf" ? (
                  <>
                    <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-red-500 text-white">
                      <FileText size={19} />
                    </span>
                    <h2 className="mt-5 font-display text-xl font-bold leading-snug text-slate-900 sm:text-2xl">
                      {s.title}
                    </h2>
                    <a
                      href={`${API_BASE}/stories/file/${s.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`story-pdf-${i}`}
                      className="group mt-5 inline-flex items-center gap-2 rounded-full bg-sky-700 px-6 py-3 text-sm font-bold text-white transition-colors duration-200 hover:bg-sky-800"
                    >
                      Read the story PDF
                      <ArrowUpRight size={15} className="transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                    </a>
                    <div className="mt-7 border-t border-slate-100 pt-5">
                      <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                        {s.author || "Jyoti Academy"}
                        {s.created_at && ` · ${new Date(s.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}`}
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <Quote size={26} className="text-amber-500" />
                    <p className="mt-5 whitespace-pre-line font-display text-lg italic leading-relaxed text-slate-700 sm:text-xl" data-testid={`story-body-${i}`}>
                      {s.body}
                    </p>
                    <div className="mt-7 border-t border-slate-100 pt-5">
                      <h2 className="font-display text-lg font-bold text-slate-900">{s.title}</h2>
                      <p className="mt-1 text-xs font-semibold uppercase tracking-widest text-slate-500">
                        {s.author || "Jyoti Academy"}
                        {s.created_at && ` · ${new Date(s.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}`}
                      </p>
                    </div>
                  </>
                )}
              </motion.article>
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
